// Bucket-2A account-specific warnings for the manual / adjusting journal-entry
// form (JE-PROVENANCE-1, Part 2).
//
// "Bucket-2A" accounts are General Ledger control accounts that a dedicated
// Nimos subledger/domain maintains (AR aging, AP aging, inventory movements,
// deposit records, preorder obligations). Posting a manual JE to one of them
// moves the GL only -- it does NOT touch the owning subledger -- so the form
// surfaces a compact, warning-only notice. Nothing here blocks the post; the
// backend accepts the entry either way.
//
// Identification is data-driven off the persisted, dedicated `kind` from
// seed_catalog.py (each of the six categories owns a distinct kind), gated on
// `is_system` so a user-created account that merely happens to reuse a reserved
// kind string cannot false-positive. This is deliberately *warning-strength*
// classification: acceptable here precisely because nothing is blocked (unlike
// the JE-CONTROL-FA-1 fa_role guard, which blocks and therefore uses a
// stronger per-account backend marker).
//
// NOT included (and must never warn here):
//   * Fixed Asset cost / Accumulated Depreciation (fa_role) -- already removed
//     from the picker entirely by JE-CONTROL-FA-1, and their kinds
//     (fixed_asset / contra_asset) are not in this map anyway.
//   * Retained Earnings (kind "retained_earnings") -- not in this map.
//   * Any Bucket-1 (ordinary) account.

export type Bucket2aAccountLike = {
  kind?: string | null;
  is_system?: boolean | null;
};

// kind -> the account-specific clause that completes the warning sentence
// ("...it will not update <clause>.").
export const BUCKET_2A_CLAUSES: Readonly<Record<string, string>> = {
  ar: "customer balances and AR aging",
  ap: "vendor balances and AP aging",
  inventory: "stock quantities, inventory movements, or average cost",
  vendor_deposits: "individual deposit records and remaining balances",
  customer_deposits: "individual deposit records and remaining balances",
  preorder: "preorder records and obligations",
};

// Returns the account-specific clause for a Bucket-2A account, or null for any
// account that should NOT show the warning (Bucket-1, FA-managed, Retained
// Earnings, non-system accounts, or nothing selected).
export function bucket2aClauseForAccount(
  account: Bucket2aAccountLike | null | undefined,
): string | null {
  if (!account) return null;
  // Only the seeded, system-owned control accounts are subledger-backed.
  if (account.is_system !== true) return null;
  const kind = (account.kind ?? "").trim().toLowerCase();
  return BUCKET_2A_CLAUSES[kind] ?? null;
}

export function isBucket2aAccount(
  account: Bucket2aAccountLike | null | undefined,
): boolean {
  return bucket2aClauseForAccount(account) !== null;
}
